import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useModal } from 'components/modals/ModalContext';
import { checkRequiredFeatureActive, FeatureCheckContext } from 'services/permissions/permissionChecks';
import { fetchData } from 'services/api/fetchData';
import { saveData } from 'services/api/saveData';
import { fetchWidgetData } from 'components/widgets/fetchWidgetData';
import { useSettings } from 'services/settings/SettingsContext';
import { formatChartAxisDate } from 'services/utils/dateTimeUtils';
import { widgetCatalog, JOB_STATUS_BREAKDOWN_LABELS } from './dashboardConfig';
import { getPeriodDates } from './getPeriodDates';
import { AdvancedWidgetConfig, CombinedWidgetDataType, DashboardLayoutItem, WidgetConfig } from 'types/WidgetTypes';
import { TimeSeriesPoint } from 'components/widgets/charts/TimeSeriesChart';
import { BreakdownDataPoint } from 'components/widgets/charts/BreakdownChart';
import Greeting from './Greeting';
import BaseWidget from 'components/widgets/BaseWidget';
import MetricChartWidget from 'components/widgets/charts/MetricChartWidget';
import JobStatusBar from './JobStatusBar';
import ToggleGroup from 'components/controls/ToggleGroup';
import DivisionSelector, { ALL_DIVISIONS_VALUE } from './DivisionSelector';
import FocusPanel from './FocusPanel';
import AdvancedWidgetsGrid, { GRID_COLUMNS } from './AdvancedWidgetsGrid';
import CustomizeDashboardModal from './CustomizeDashboardModal';
import IconButton from 'components/buttons/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTableCellsLarge } from '@fortawesome/free-solid-svg-icons';
import '../../style/scss/dashboard.scss';

type DashboardPeriod = 'week' | 'month';

// The default widgets are packed into the middle 4 of the 6 grid columns,
// matching the width the regular, non custom dashboard sits in
const DEFAULT_LAYOUT_X_OFFSET = 1;
const DEFAULT_LAYOUT_WIDTH = GRID_COLUMNS - (DEFAULT_LAYOUT_X_OFFSET * 2);

// Lays the default enabled widgets out left to right, wrapping at the centered column span
const buildDefaultLayout = (widgets: AdvancedWidgetConfig[]): DashboardLayoutItem[] => {
    let cursorX = 0;
    let cursorY = 0;
    let rowHeight = 0;

    return widgets.map(widget => {
        const { w, h } = widget.defaultGridSize;

        // Wrap to a new row once the widget no longer fits on the current one
        if (cursorX + w > DEFAULT_LAYOUT_WIDTH) {
            cursorX = 0;
            cursorY += rowHeight;
            rowHeight = 0;
        }

        const item = { widget_key: widget.widgetKey, x: DEFAULT_LAYOUT_X_OFFSET + cursorX, y: cursorY, w, h };
        cursorX += w;
        rowHeight = Math.max(rowHeight, h);
        return item;
    });
};

const Dashboard: React.FC = () => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();
    const { activeFeatures, roleFeatures, allowedFeatures, handleLogout } = useAuthContext();
    const { initializeModal } = useModal();
    const featureContext: FeatureCheckContext = { activeFeatures, roleFeatures, allowedFeatures };
    const customDashboardActive = checkRequiredFeatureActive('custom_dashboard', featureContext);

    const [period, setPeriod] = useState<DashboardPeriod>('week');
    const [division, setDivision] = useState<string>(ALL_DIVISIONS_VALUE);
    const periodDates = getPeriodDates(period);
    const { currentPeriod } = periodDates;

    const [widgetData, setWidgetData] = useState<CombinedWidgetDataType>({});
    const [widgetLoading, setWidgetLoading] = useState<boolean>(true);
    const [chartData, setChartData] = useState<{ [chartKey: string]: TimeSeriesPoint[] | BreakdownDataPoint[] }>({});
    const [chartLoading, setChartLoading] = useState<boolean>(true);

    const [advancedLayout, setAdvancedLayout] = useState<DashboardLayoutItem[]>([]);
    const [advancedLayoutLoaded, setAdvancedLayoutLoaded] = useState<boolean>(false);

    // Extra params applied to every widget and chart fetch, carrying the division filter
    const extraParams = division === ALL_DIVISIONS_VALUE ? undefined : { division };

    // The static dashboard only shows the default widgets whose required feature is active
    const defaultWidgets = widgetCatalog.filter(widget => widget.defaultEnabled);
    const visibleNumberWidgets = defaultWidgets.filter(
        widget => widget.widgetType === 'number' && checkRequiredFeatureActive(widget.requiredFeature, featureContext)
    );
    const visibleChartWidgets = defaultWidgets.filter(
        widget => (widget.widgetType === 'timeSeries' || widget.widgetType === 'jobStatusBar')
            && checkRequiredFeatureActive(widget.requiredFeature, featureContext)
    );

    // Persist the advanced dashboard layout to the server
    const saveAdvancedLayout = async (layout: DashboardLayoutItem[]) => {
        await saveData({ apiUrl: 'patch_dashboard_preference', method: 'patch', data: { widgets: layout } });
    };

    // Fetch the number tile widget data for the static dashboard
    useEffect(() => {
        if (customDashboardActive) {
            return;
        }

        const fetchTiles = async () => {
            const numberWidgetConfigs: WidgetConfig[] = visibleNumberWidgets.map(widget => ({
                widgetKey: widget.widgetKey,
                variant: widget.variant || 'numberWidget',
                title: widget.title,
                tooltipText: widget.tooltipText,
                query: widget.query!,
                calculationMethod: widget.calculationMethod || 'count',
                prefix: widget.prefix,
                measuringUnit: widget.measuringUnit,
                decimals: widget.decimals,
                moreIsNegative: widget.moreIsNegative,
            }));
            const allWidgetsData = await fetchWidgetData(numberWidgetConfigs, periodDates, handleLogout, extraParams);
            setWidgetData(allWidgetsData);
            setWidgetLoading(false);
        };

        setWidgetLoading(true);
        fetchTiles();
    }, [activeFeatures, period, division, customDashboardActive]);

    // Fetch the chart data for the static dashboard
    useEffect(() => {
        if (customDashboardActive) {
            return;
        }

        const fetchCharts = async () => {
            const newChartData: { [chartKey: string]: TimeSeriesPoint[] | BreakdownDataPoint[] } = {};
            const divisionParam = division === ALL_DIVISIONS_VALUE ? '' : `&division=${division}`;

            await Promise.all(visibleChartWidgets.map(async widget => {
                const endpoint = `${widget.endpoint}/?from=${currentPeriod.from}&to=${currentPeriod.to}${divisionParam}`;
                const response = await fetchData({ apiUrl: endpoint, handleLogout });
                newChartData[widget.widgetKey] = response || [];
            }));

            setChartData(newChartData);
            setChartLoading(false);
        };

        setChartLoading(true);
        fetchCharts();
    }, [activeFeatures, period, division, customDashboardActive]);

    // Fetch the saved advanced dashboard layout of the current user, seeding the
    // default widgets whenever the saved layout is still empty
    useEffect(() => {
        const fetchPreference = async () => {
            const response = await fetchData({ apiUrl: 'get_dashboard_preference', handleLogout });
            const savedLayout: DashboardLayoutItem[] = response?.widgets || [];

            if (savedLayout.length === 0) {
                const seededLayout = buildDefaultLayout(
                    defaultWidgets.filter(widget => checkRequiredFeatureActive(widget.requiredFeature, featureContext))
                );
                setAdvancedLayout(seededLayout);
                saveAdvancedLayout(seededLayout);
            } else {
                setAdvancedLayout(savedLayout);
            }

            setAdvancedLayoutLoaded(true);
        };

        if (customDashboardActive) {
            fetchPreference();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customDashboardActive]);

    // Update the layout while dragging or resizing, without saving on every intermediate step
    const handleLayoutChange = (layout: DashboardLayoutItem[]) => {
        setAdvancedLayout(layout);
    };

    // Save the layout once a drag or resize is released, or a widget is added or removed
    const handleSaveLayout = (layout: DashboardLayoutItem[]) => {
        setAdvancedLayout(layout);
        saveAdvancedLayout(layout);
    };

    // Open the widget catalog modal
    const openCustomizeModal = () => {
        initializeModal(
            <CustomizeDashboardModal initialLayout={advancedLayout} onSave={handleSaveLayout} />,
            { title: 'dashboard.advanced_widget.catalog_title', modalSize: 'medium-large' }
        );
    };

    return (
        <>
            <div className='container-filled'>
                <div className='dashboard'>
                    <div className='dashboard-header'>
                        <div className='greeting'>
                            <h2>
                                <Greeting />
                            </h2>
                        </div>
                        <div className='dashboard-controls'>
                            <ToggleGroup
                                options={[
                                    { value: 'week', label: t('date_time.general.week') },
                                    { value: 'month', label: t('date_time.general.month') },
                                ]}
                                value={period}
                                onChange={(value) => setPeriod(value as DashboardPeriod)}
                            />
                            <DivisionSelector selectedDivision={division} onChange={setDivision} />
                            {customDashboardActive && (
                                <IconButton
                                    icon={<FontAwesomeIcon icon={faTableCellsLarge} />}
                                    tooltipText='dashboard.advanced_widget.add_widgets_tooltip'
                                    onClick={openCustomizeModal}
                                    size='small'
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {customDashboardActive ? (
                <div className='container-dashboard-grid'>
                    <div className='dashboard'>
                        <div className='advanced-widgets-section'>
                            {advancedLayoutLoaded ? (
                                <AdvancedWidgetsGrid
                                    layout={advancedLayout}
                                    onLayoutChange={handleLayoutChange}
                                    onSaveLayout={handleSaveLayout}
                                    period={period}
                                    periodDates={periodDates}
                                    handleLogout={handleLogout}
                                    extraParams={extraParams}
                                />
                            ) : (
                                <div className='advanced-widgets-loader'>
                                    <div className='loader' />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div className='container-filled'>
                    <div className='dashboard'>
                        <div className='widgets-section'>
                            <h5>{t('dashboard.general.highlights_title')}</h5>
                            <div className='widgets-row'>
                                {visibleNumberWidgets.map(widget => (
                                    <BaseWidget
                                        key={widget.widgetKey}
                                        widgetKey={widget.widgetKey}
                                        variant={widget.variant || 'numberWidget'}
                                        title={widget.title}
                                        tooltipText={widget.tooltipText}
                                        period={period}
                                        query={widget.query!}
                                        data={widgetData[widget.widgetKey]}
                                        loading={widgetLoading}
                                        calculationMethod={widget.calculationMethod || 'count'}
                                        prefix={widget.prefix}
                                        measuringUnit={widget.measuringUnit}
                                        decimals={widget.decimals}
                                        moreIsNegative={widget.moreIsNegative}
                                    />
                                ))}
                            </div>
                            <div className='charts-stack'>
                                {visibleChartWidgets.map(widget => (
                                    widget.widgetType === 'jobStatusBar' ? (
                                        <JobStatusBar
                                            key={widget.widgetKey}
                                            data={(chartData[widget.widgetKey] as BreakdownDataPoint[]) || []}
                                            groupLabels={Object.fromEntries(
                                                Object.entries(JOB_STATUS_BREAKDOWN_LABELS).map(([key, label]) => [key, t(label)])
                                            )}
                                            loading={chartLoading}
                                            periodFrom={currentPeriod.from}
                                            periodTo={currentPeriod.to}
                                        />
                                    ) : (
                                        <MetricChartWidget
                                            key={widget.widgetKey}
                                            title={t(widget.title)}
                                            titleClassName='title'
                                            data={(chartData[widget.widgetKey] as TimeSeriesPoint[]) || []}
                                            series={(widget.series || []).map(series => ({ ...series, label: t(series.label) }))}
                                            loading={chartLoading}
                                            height={260}
                                            xFormatter={(value) => formatChartAxisDate(value, userLocale)}
                                            allowDecimals={widget.allowDecimals}
                                            yFormatter={(value) => Math.round(value).toString()}
                                        />
                                    )
                                ))}
                            </div>
                        </div>
                        <FocusPanel />
                    </div>
                </div>
            )}
        </>
    );
};

export default Dashboard;
